/**
 * Admin Panel - Typesense Management API
 *
 * Zod schemas for Typesense search index management (chat messages, tools/plugins, Pipedream).
 * Part of the NexusApi system for end-to-end type safety.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

// ============================================================
// Schemas - Chat Messages
// ============================================================

/**
 * Typesense chat messages stats
 */
export const TypesenseStatsSchema = z.object({
  queue: z.object({
    pending: z.number(),
    processing: z.number(),
    completed: z.number(),
    failed: z.number()
  }),
  database: z.object({
    totalMessages: z.number()
  }),
  timestamp: z.string()
});

/**
 * Backfill request params for chat messages
 */
export const TypesenseBackfillMessagesParamsSchema = z.object({
  sinceDate: z.string().optional(),
  batchSize: z.number().optional()
});

/**
 * Backfill result response.
 *
 * `"error"` is gone. Only the tools backfill ever set it, and only for the
 * "Typesense not configured" case, which is now a 503 — a backfill that indexes
 * nothing is work not done, and both the panel and the admin agent read a 2xx as
 * done. The messages backfill never set it at all.
 */
export const TypesenseBackfillResultSchema = z.object({
  status: z.enum(["started", "skipped"]),
  message: z.string(),
  totalMessages: z.number().optional(),
  totalTools: z.number().optional(),
  estimatedBatches: z.number().optional(),
  batchSize: z.number().optional(),
  sinceDate: z.string().optional()
});

// ============================================================
// Schemas - Tools/Plugins
// ============================================================

/**
 * Typesense tools collection stats.
 *
 * `enabled` is whether a client was constructed at all — i.e. whether
 * `TYPESENSE_URL` is set — NOT whether the server answers. `getStats()` absorbs
 * a failed retrieve into `numDocuments: 0`, so an unreachable Typesense and an
 * empty collection look identical here. Use `HealthCheck` to tell them apart.
 *
 * `typesense` is not nullable: the handler always passes the service's object.
 * It was declared `.nullable()` and `enabled` was omitted entirely, so the field
 * shipped on the wire and the generated client stripped it on every call —
 * invisible to `tsc` because the value reaches the response literal through a
 * variable, which loses excess-property checking.
 */
export const TypesenseToolsStatsSchema = z.object({
  typesense: z.object({
    numDocuments: z.number(),
    enabled: z.boolean()
  }),
  database: z.object({
    totalPublicTools: z.number()
  }),
  timestamp: z.string()
});

/**
 * What a health probe of the tools collection can honestly report.
 *
 * - `reachable` — the collection was retrieved; `numDocuments` is real.
 * - `unreachable` — a client exists but the server did not answer; `detail`
 *   carries the underlying error.
 * - `unconfigured` — no `TYPESENSE_URL`, so tools search runs on the PostgreSQL
 *   fallback. Not a fault, and deliberately not the same as `unreachable`.
 */
export const TYPESENSE_HEALTH_STATUSES = ["reachable", "unreachable", "unconfigured"] as const;
export const TypesenseHealthStatusSchema = z.enum(TYPESENSE_HEALTH_STATUSES);

/**
 * `numDocuments` and `detail` are null on the states where they have no value,
 * rather than 0 and "" — a health check that reports zero documents when it
 * could not count them is the failure it exists to surface.
 */
export const TypesenseHealthSchema = z.object({
  status: TypesenseHealthStatusSchema,
  numDocuments: z.number().nullable(),
  detail: z.string().nullable()
});

/**
 * Backfill request params for tools
 */
export const TypesenseBackfillToolsParamsSchema = z.object({
  batchSize: z.number().optional()
});

/**
 * Delete tools collection response.
 *
 * Success only. The handler used to catch its own failure and answer HTTP 200
 * with `status: "error"`, which every caller then had to remember to test for —
 * and which the admin agent reads as a successful delete, the same defect fixed
 * on `GET admin-panel/audit/:id` in #1898. A failed delete is now an exception
 * and reaches the client as a non-2xx, so `status` can only be `"ok"`.
 */
export const TypesenseDeleteCollectionResponseSchema = z.object({
  status: z.literal("ok"),
  message: z.string()
});

// ============================================================
// Schemas - Pipedream Categories
// ============================================================

/**
 * Pipedream categories stats
 */
export const PipedreamCategoriesStatsSchema = z.object({
  total: z.number(),
  pipedream: z.number(),
  withCategories: z.number(),
  withoutCategories: z.number(),
  sampleWithCategories: z.array(
    z.object({
      publicName: z.string(),
      categories: z.array(z.string())
    })
  ),
  timestamp: z.string()
});

/**
 * Backfill request params for Pipedream categories.
 *
 * These are QUERY parameters — the generated client puts them on the query
 * string — so every value arrives at the handler as a STRING. The previous
 * `z.number()` / `z.boolean()` declaration could therefore never match anything
 * the wire carries, and nothing on either side parsed against it: the handler
 * read the raw strings itself and the client sent whatever `parseInt` produced,
 * `NaN` included. This is the transport-shaped version of the same contract, so
 * `Query.parse` on the handler is now a real gate rather than a no-op.
 *
 * `.default(…)` reproduces the fall-throughs the handler already hardcoded, so
 * an omitted parameter behaves exactly as before. `dryRun` keeps its previous
 * `false` default deliberately — flipping an omitted flag on a write path to the
 * safe value is a behaviour change for existing callers, not a contract repair.
 *
 * `z.coerce.boolean()` is unsafe for query strings — `Boolean("false") === true`
 * — so `dryRun` is preprocessed explicitly, matching
 * `DeleteOrganizationQuerySchema` and `ListDaytonaTracesParamsSchema`.
 */
export const PipedreamBackfillCategoriesParamsSchema = z.object({
  dryRun: z.preprocess(
    (v) => (v === "true" ? true : v === "false" ? false : v),
    z.boolean().default(false)
  ),
  pageSize: z.coerce.number().int().min(1).max(1000).default(100),
  delayMs: z.coerce.number().int().min(0).max(60_000).default(300)
});

/**
 * Pipedream backfill result.
 *
 * `status` is a literal, not an enum: "started" is the only outcome a 2xx can
 * carry. The dropped "error" member was returned with HTTP 200 when Pipedream
 * auth could not be initialized, which the admin agent reads as a backfill that
 * started. That case is now a 503.
 */
/**
 * All three settings are REQUIRED, not optional.
 *
 * They arrive as `@Query` params, and `extractMeta` never receives `query` — so
 * the audit extractor's only honest source for them is the response. While
 * `dryRun` was optional the extractor read it off `body` instead, where it does
 * not exist on a query-only POST, so it evaluated to `false` on every call and a
 * dry run that changed nothing was audited as a completed backfill. The handler
 * has always set all three; requiring them here is what makes the audit row
 * unable to lie about the mode.
 */
export const PipedreamBackfillResultSchema = z.object({
  status: z.literal("started"),
  message: z.string(),
  dryRun: z.boolean(),
  pageSize: z.number(),
  delayMs: z.number()
});

// ============================================================
// ZAdminTypesense - Endpoint Definitions
// ============================================================

export const ZAdminTypesense = {
  /**
   * Get Typesense chat messages stats (queue + database)
   * GET /api/admin/typesense/stats
   */
  GetStats: {
    method: "GET" as const,
    path: "/api/admin/typesense/stats",
    Response: ApiSuccessSchema(TypesenseStatsSchema)
  },

  /**
   * Probe the Typesense tools collection and report whether it answers.
   * GET /api/admin/typesense/health
   *
   * Answers 200 in all three states. That is not the `200 {error}` anti-pattern:
   * the route's job is to REPORT health, and reporting "unreachable" is that job
   * done, not failed. What made the old version dishonest was a hardcoded
   * `status: "ok"` that no outage could change.
   */
  HealthCheck: {
    method: "GET" as const,
    path: "/api/admin/typesense/health",
    Response: ApiSuccessSchema(TypesenseHealthSchema)
  },

  /**
   * Get Typesense tools collection stats
   * GET /api/admin/typesense/tools-stats
   */
  GetToolsStats: {
    method: "GET" as const,
    path: "/api/admin/typesense/tools-stats",
    Response: ApiSuccessSchema(TypesenseToolsStatsSchema)
  },

  /**
   * Trigger backfill of chat messages to Typesense
   * POST /api/admin/typesense/backfill
   */
  BackfillMessages: {
    method: "POST" as const,
    path: "/api/admin/typesense/backfill",
    Params: TypesenseBackfillMessagesParamsSchema,
    Response: ApiSuccessSchema(TypesenseBackfillResultSchema)
  },

  /**
   * Trigger backfill of tools to Typesense
   * POST /api/admin/typesense/backfill-tools
   */
  BackfillTools: {
    method: "POST" as const,
    path: "/api/admin/typesense/backfill-tools",
    Params: TypesenseBackfillToolsParamsSchema,
    Response: ApiSuccessSchema(TypesenseBackfillResultSchema)
  },

  /**
   * Delete entire tools collection (nuke)
   * DELETE /api/admin/typesense/tools-collection
   */
  DeleteToolsCollection: {
    method: "DELETE" as const,
    path: "/api/admin/typesense/tools-collection",
    Response: ApiSuccessSchema(TypesenseDeleteCollectionResponseSchema)
  },

  /**
   * Get Pipedream categories stats
   * GET /api/admin/pipedream/categories-stats
   */
  GetPipedreamCategoriesStats: {
    method: "GET" as const,
    path: "/api/admin/pipedream/categories-stats",
    Response: ApiSuccessSchema(PipedreamCategoriesStatsSchema)
  },

  /**
   * Trigger Pipedream categories backfill
   * POST /api/admin/pipedream/backfill-categories
   */
  BackfillPipedreamCategories: {
    method: "POST" as const,
    path: "/api/admin/pipedream/backfill-categories",
    Params: PipedreamBackfillCategoriesParamsSchema,
    Response: ApiSuccessSchema(PipedreamBackfillResultSchema)
  }
} as const;

// ============================================================
// Convenience Type Exports
// ============================================================

export type TypesenseStats = z.infer<typeof TypesenseStatsSchema>;
export type TypesenseToolsStats = z.infer<typeof TypesenseToolsStatsSchema>;
export type TypesenseHealth = z.infer<typeof TypesenseHealthSchema>;
export type TypesenseHealthStatus = z.infer<typeof TypesenseHealthStatusSchema>;
export type TypesenseBackfillResult = z.infer<typeof TypesenseBackfillResultSchema>;
export type PipedreamCategoriesStats = z.infer<typeof PipedreamCategoriesStatsSchema>;
export type PipedreamBackfillResult = z.infer<typeof PipedreamBackfillResultSchema>;
